/**
 * Turns whatever an RTK Query import mutation rejected with into the text
 * shown to the person importing. LiftCloud answers a failed import with
 * `{ status: "error", message, details? }`, so `message` is the real cause and
 * is passed through untouched; the other shapes (a body that is not JSON, a
 * request that never reached the server) get a sentence of their own rather
 * than the bare fallback, which tells the reader nothing.
 */

type ErrorRecord = Record<string, unknown>;

const HTML_BODY = /^\s*<(!doctype|html|head|body)/i;

const isRecord = (value: unknown): value is ErrorRecord =>
  typeof value === "object" && value !== null;

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;

const httpStatusOf = (err: ErrorRecord): number | undefined => {
  if (typeof err.originalStatus === "number") return err.originalStatus;
  if (typeof err.status === "number") return err.status;
  return undefined;
};

const withStatus = (fallback: string, status: number | undefined): string =>
  status === undefined ? fallback : `${fallback} (HTTP ${status})`;

const bodyMessage = (data: unknown): string | undefined => {
  if (!isRecord(data)) return nonEmptyString(data);
  const message = nonEmptyString(data.message) ?? nonEmptyString(data.error);
  const details = nonEmptyString(data.details);
  if (message && details) return `${message}: ${details}`;
  return message ?? details;
};

function importErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return nonEmptyString(err.message) ?? fallback;
  if (!isRecord(err)) return nonEmptyString(err) ?? fallback;

  const fromBody = bodyMessage(err.data);
  if (fromBody && !HTML_BODY.test(fromBody)) return fromBody;

  if (err.status === "FETCH_ERROR") {
    const reason = nonEmptyString(err.error);
    return reason ? `Could not reach the server: ${reason}` : "Could not reach the server";
  }

  return withStatus(fallback, httpStatusOf(err));
}

export default importErrorMessage;
